import React, { useState, useEffect, useRef } from "react";
import {
  IconSun,
  IconMoon,
  IconLogout,
  IconUser,
  IconSettings,
  IconMenu2,
  IconX,
} from '@tabler/icons-react';
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import NotificationsDropdown from '../ui/NotificationsDropdown';

interface NavbarProps {
  isSidebarOpen: boolean;
  onMenuToggle: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ isSidebarOpen, onMenuToggle }) => {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const canSeeNotifications = hasPermission('notifications_view');
  // Cerrar menú al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  // ✅ OBTENER EL PRIMER ROL DEL USUARIO
  const getUserRole = () => {
    if (!user?.roles || user.roles.length === 0) {
      return "Usuario";
    }

    const role = user.roles[0];
    const roleMap: Record<string, string> = {
      super_admin: "Super Administrador",
      admin: "Administrador",
      administrador_comercial: "Administrador Comercial",
      support_n1: "Soporte N1",
      support_n2: "Soporte N2",
      ti_n1: "TI N1",
      ti_n2: "TI N2",
      ti_admin: "TI Admin",
    };

    return roleMap[role.code] || role.name || "Usuario";
  };

  // ✅ OBTENER EL COLOR DEL ROL
  const getRoleColor = () => {
    if (!user?.roles || user.roles.length === 0) {
      return "text-content-secondary";
    }

    const role = user.roles[0];
    const colorMap: Record<string, string> = {
      super_admin: "text-brand-secondary",
      admin: "text-state-success",
      administrador_comercial: "text-brand-primary",
      support_n1: "text-state-warning",
      support_n2: "text-state-warning",
      ti_n1: "text-state-info",
      ti_n2: "text-state-info",
      ti_admin: "text-brand-secondary",
    };

    return colorMap[role.code] || "text-content-secondary";
  };

  // ✅ OBTENER EL NOMBRE DEL USUARIO
  const getUserName = () => {
    if (user?.name) {
      return user.name;
    }
    return "Administrador";
  };

  // ✅ OBTENER INICIALES DEL USUARIO
  const getUserInitials = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return "AD";
  };

  return (
    <nav className="sticky top-0 z-20 border-b border-border bg-surface transition-colors">
      <div className="flex min-h-[var(--navigation-header-height)] items-center justify-between px-4">
        {/* LADO IZQUIERDO */}
        <div className="flex items-center gap-6">
          <button
            aria-controls="primary-navigation"
            aria-expanded={isSidebarOpen}
            aria-label={isSidebarOpen ? "Cerrar menú" : "Abrir menú"}
            className="rounded-ui p-2 text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary md:hidden"
            onClick={onMenuToggle}
            type="button"
          >
            {isSidebarOpen ? <IconX className="h-6 w-6" /> : <IconMenu2 className="h-6 w-6" />}
          </button>
          <div className="hidden lg:block">
            <h2 className="text-lg font-semibold leading-tight text-content-primary">
              ¡Hola, {getUserName()}! 👋
            </h2>
          </div>
        </div>

        {/* LADO DERECHO */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Toggle de Tema */}
          <button
            onClick={toggleTheme}
            className="relative overflow-hidden rounded-ui p-2.5 text-content-secondary transition-colors hover:bg-surface-hover hover:text-brand-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
            title="Cambiar modo"
          >
            <div
              className={`transition-transform duration-500 ${theme === "light" ? "translate-y-0" : "-translate-y-10"}`}
            >
              <IconMoon className="w-5 h-5" />
            </div>
            <div
              className={`absolute top-2.5 transition-transform duration-500 ${theme === "dark" ? "translate-y-0" : "translate-y-10"}`}
            >
              <IconSun className="w-5 h-5 text-amber-400" />
            </div>
          </button>

        {/* ✅ Notificaciones*/}
        {canSeeNotifications && <NotificationsDropdown />}

          {/* Separador */}
          <div className="mx-1 hidden h-8 w-px bg-border sm:block"></div>

          {/* Perfil de Usuario con Menú Desplegable */}
          <div className="relative" ref={menuRef}>
            <div
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 pl-2 group cursor-pointer"
            >
              <div className="hidden sm:block text-right">
                <p className="text-sm font-semibold text-content-primary transition-colors group-hover:text-brand-primary">
                  {getUserName()}
                </p>
                <p className={`text-[10px] font-bold uppercase tracking-tighter ${getRoleColor()}`}>
                  {getUserRole()}
                </p>
              </div>

              <div className="relative">
                <div className="flex h-10 w-10 items-center justify-center rounded-ui bg-brand-primary font-semibold text-white shadow-ui transition-transform group-hover:scale-105">
                  {getUserInitials()}
                </div>
                <div className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full border-2 border-surface bg-state-success"></div>
              </div>
            </div>

            {/* Menú Desplegable */}
            {showUserMenu && (
              <div className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-card border border-border bg-surface py-2 shadow-raised animate-fadeIn">
                {/* Cabecera del menú */}
                <div className="border-b border-border px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-ui bg-brand-primary font-semibold text-white">
                      {getUserInitials()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-content-primary">
                        {getUserName()}
                      </p>
                      <p className="text-xs text-content-secondary">
                        {user?.email || "admin@gmail.com"}
                      </p>
                      <p className={`text-[10px] font-bold uppercase ${getRoleColor()}`}>
                        {getUserRole()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Opciones del menú */}
                <div className="py-2">
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate("/perfil");
                    }}
                    className="flex w-full items-center gap-3 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary"
                  >
                    <IconUser className="w-4 h-4" />
                    <span>Mi Perfil</span>
                  </button>

                  {/* <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate("/configuracion");
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <IconSettings className="w-4 h-4" />
                    <span>Configuración</span>
                  </button> */}
                </div>

                {/* Separador y botón de cerrar sesión */}
                <div className="border-t border-border pt-2">
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 px-4 py-2 text-sm text-state-danger transition-colors hover:bg-state-danger-soft"
                  >
                    <IconLogout className="w-4 h-4" />
                    <span>Cerrar sesión</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
